import { useEffect, useState } from 'react';

import { Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';

import {
  currentUserApi,
  EnableMfaResponse,
  MfaSettingsStatusResponse,
  MfaStatusResponse,
} from '../../axios/currentUser';
import { useLanguage } from '../../hooks';
import { MfaActivate } from './MfaActivate';
import { MfaQr } from './MfaQr';
import { MfaSuccess } from './MfaSuccess';
import { MfaVerifyCode } from './MfaVerifyCode';

const customStyles = {
  modal: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      padding: '0px',
      maxWidth: '572px',
      width: '100%',
      height: 'auto',
      background: '#FFFFFF',
      margin: '16px',
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      '@media (max-width: 600px)': {
        borderRadius: '0px',
        maxWidth: '100vw',
        width: '100vw',
        height: '100vh',
        margin: '0px',
        position: 'fixed',
        top: '0px',
        left: '0px',
        right: '0px',
        bottom: '0px',
      },
    },
    '& .MuiDialogContent-root': {
      padding: '0px',
      height: 'auto',
      '@media (max-width: 600px)': {
        height: '100vh',
      },
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '3px',
    padding: '22px 28px',
    height: 'auto',
    width: '100%',
    justifyContent: 'flex-start',
    '@media (max-width: 600px)': {
      padding: '0px',
      height: '100vh',
      width: '100vw',
    },
  },
  title: {
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '0%',
    alignSelf: 'flex-start',
    '@media (max-width: 600px)': {
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  actionButton: {
    borderRadius: '18px',
    padding: '8px 18px',
    height: '40px',
    width: '100%',
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'Medium',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0%',
  },
  error: {
    marginTop: 2,
    marginBottom: 2,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '33px 50.5px',
    gap: '24px',
    width: '100%',
    '@media (max-width: 600px)': {
      padding: '24px 21px',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    padding: '0px',
    '@media (max-width: 600px)': {
      padding: '24px 21px 0px 21px',
    },
  },
  closeButton: {
    padding: '0px',
    margin: '0px',
    width: '40px',
    height: '40px',
    backgroundColor: '#EAEDF3',
    borderRadius: '50%',
    '@media (max-width: 600px)': {
      width: '32px',
      height: '32px',
    },
  },
  closeIcon: {
    color: '#17181A',
    height: '24px',
    width: '24px',
  },
  maybeLaterButton: {
    borderColor: '#D1D4D9',
    color: '#46484D',
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'Medium',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0%',
  },
};

interface MfaModalProps {
  open: boolean;
  onClose: () => void;
  isMobile?: boolean;
  showActivateInSuccess?: boolean;
}

type MfaStep = 'qr' | 'code' | 'success' | 'activate';

export function MfaModal({
  open,
  onClose,
  isMobile = false,
  showActivateInSuccess = false,
}: MfaModalProps) {
  const lang = useLanguage();
  const [mfaStatus, setMfaStatus] = useState<MfaStatusResponse | null>(null);
  const [mfaSettingsStatus, setMfaSettingsStatus] = useState<MfaSettingsStatusResponse | null>(
    null,
  );
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [currentStep, setCurrentStep] = useState<MfaStep>(
    showActivateInSuccess ? 'activate' : 'qr',
  );
  const [mfaState, setMfaState] = useState({
    loading: false,
    error: '',
    qrData: '',
    secretCode: '',
    deviceName: '',
  });

  const getMfaSettingStatus = async () => {
    const mfaSettingStatusFromApi = await currentUserApi.getMfaSettingsStatus();
    if (mfaSettingStatusFromApi.settings) {
      setMfaSettingsStatus(mfaSettingStatusFromApi as MfaSettingsStatusResponse);
    }
    if (mfaSettingStatusFromApi.settings === 'disabled') {
      return;
    }
  };

  const getMfaStatus = async () => {
    try {
      const data = await currentUserApi.getMfaStatus();
      setMfaStatus(data);
      setStatusLoading(false);
    } catch {
      setStatusError(lang.login.mfaStatusError);
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    getMfaSettingStatus();
    getMfaStatus();
    setMfaState((prev) => ({ ...prev, error: '' }));
    handleOpenSetup();
  }, [open]);

  const handleOpenSetup = async () => {
    setMfaState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const res: EnableMfaResponse = await currentUserApi.enableMfa();
      setMfaState((prev) => ({
        ...prev,
        loading: false,
        qrData: res.qr_code_data,
        secretCode: res.secret_code,
        deviceName: res.device_name,
        error: '',
      }));
    } catch (err: any) {
      setMfaState((prev) => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.error || lang.login.mfaEnableError,
      }));
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(mfaState.secretCode);
  };

  const handleVerifyCode = async (code: string): Promise<boolean> => {
    setMfaState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await currentUserApi.verifyMfa(code, mfaState.deviceName);
      if (res.success) {
        setMfaState((prev) => ({ ...prev, loading: false }));
        setCurrentStep('success');
        return true;
      } else {
        setMfaState((prev) => ({
          ...prev,
          loading: false,
          error: res.message || lang.login.mfaVerifyError,
        }));
        return false;
      }
    } catch (err: any) {
      setMfaState((prev) => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.error || lang.login.mfaVerifyError,
      }));
      return false;
    }
  };

  const handleNext = () => {
    if (currentStep === 'activate') {
      setCurrentStep('qr');
      return;
    }
    if (currentStep === 'qr') {
      setCurrentStep('code');
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    // Prevent closing when clicking outside or pressing escape
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (mfaSettingsStatus?.settings === 'mandatory') return;
    }
    setCurrentStep('qr');
    setMfaState((prev) => ({ ...prev, error: '' }));
    onClose();
  };

  const getStepContent = () => {
    if (mfaStatus?.mfa_enabled) {
      return <MfaSuccess isMobile={isMobile} />;
    }
    switch (currentStep) {
      case 'activate':
        return <MfaActivate settingsMode={mfaSettingsStatus?.settings} />;
      case 'qr':
        return (
          <MfaQr
            qrData={mfaState.qrData}
            isLoading={mfaState.loading}
            secretCode={mfaState.secretCode}
            handleCopyCode={handleCopyCode}
          />
        );
      case 'code':
        return (
          <MfaVerifyCode
            onVerify={handleVerifyCode}
            error={mfaState.error}
            isLoading={mfaState.loading}
          />
        );
      case 'success':
        return <MfaSuccess isMobile={isMobile} />;
      default:
        return;
    }
  };

  const getButtonText = () => {
    switch (currentStep) {
      case 'activate':
        return lang.mfa.activateNow;
      case 'qr':
        return lang.mfa.next;
      case 'code':
        return lang.mfa.finish;
      case 'success':
        return lang.mfa.close;
      default:
        return lang.mfa.close;
    }
  };

  const isButtonDisabled = () => {
    if (mfaStatus?.mfa_enabled) return false;
    if (mfaState.loading) return true;
    if (currentStep === 'qr') return mfaState.loading || !mfaState.qrData;
    return false;
  };

  const showButton =
    (currentStep === 'success' && !isMobile) || currentStep === 'qr' || currentStep === 'activate';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      sx={customStyles.modal}
    >
      <DialogContent>
        <Box sx={customStyles.container}>
          <Box sx={customStyles.header}>
            <Typography sx={customStyles.title}>{lang.mfa.title}</Typography>
            {mfaSettingsStatus?.settings === 'optional' && (
              <IconButton
                onClick={handleClose}
                sx={customStyles.closeButton}
              >
                <Close sx={customStyles.closeIcon} />
              </IconButton>
            )}
          </Box>
          <Box sx={customStyles.contentContainer}>
            {getStepContent()}
            {showButton && (
              <Button
                variant='contained'
                onClick={currentStep === 'success' ? handleClose : handleNext}
                disabled={isButtonDisabled()}
                sx={customStyles.actionButton}
              >
                {mfaState.loading || statusLoading ? (
                  <CircularProgress
                    size={20}
                    color='inherit'
                  />
                ) : (
                  getButtonText()
                )}
              </Button>
            )}
            {currentStep === 'activate' && mfaSettingsStatus?.settings === 'optional' && (
              <Button
                variant='outlined'
                onClick={handleClose}
                sx={customStyles.maybeLaterButton}
              >
                {lang.mfa.maybeLater}
              </Button>
            )}
            {statusError && (
              <Alert
                severity='error'
                sx={customStyles.error}
              >
                {statusError}
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
